import { useCallback, useState } from "react"
import { useClient } from "urql"

import type {
  InventoryByPalletItem,
  InventoryByPalletListFailure,
  InventoryByPalletListSuccess,
} from "@/graphql/codegen/graphql"
import { GetInventoryPalletsForPrintDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetInventoryPalletsForPrintReturn {
  fetchPallets: (productId: number, palletReferenceNumbers: number[]) => Promise<InventoryByPalletItem[]>
  isFetching: boolean
}

export const useGetInventoryPalletsForPrint = (): UseGetInventoryPalletsForPrintReturn => {
  const client = useClient()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isFetching, setIsFetching] = useState(false)

  const fetchPallets = useCallback(
    async (productId: number, palletReferenceNumbers: number[]): Promise<InventoryByPalletItem[]> => {
      setIsFetching(true)

      const { data, error } = await client
        .query(
          GetInventoryPalletsForPrintDocument,
          {
            input: {
              filter: { productId, referenceNbrs: palletReferenceNumbers },
              limit: palletReferenceNumbers.length,
            },
          },
          { requestPolicy: "network-only" }
        )
        .toPromise()

      setIsFetching(false)
      const output = data?.inventory.listByPallet
      const hasError = error || output?.__typename === "InventoryByPalletListFailure"

      if (hasError) {
        const message = error?.message ?? (output as InventoryByPalletListFailure).error.message
        notify({ type: NotificationType.Error, message })
        return []
      }

      return (output as InventoryByPalletListSuccess).items
    },
    [client, notify]
  )

  return {
    fetchPallets,
    isFetching,
  }
}
